// Modules
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, FormGroup, Icon, InputGroup, Intent } from '@blueprintjs/core'
import axios from 'axios'

// Components
import InputNotice from 'components/interface/input-notice/InputNotice'
import Logo from 'components/brand/logo/Logo'
import Page from 'components/layout/page/Page'

// Hooks
import useFormControls from 'hooks/validation/useFormControls'
import useLanguage from 'hooks/system/useLanguage'
import useNotices from 'hooks/system/useNotices'
import usePasswordReset from 'hooks/authentication/usePasswordReset'

// Helpers
import { convertFontSizeToPixelSize } from 'helpers/size'

// Constants
import { PASSWORD_RESET_CODE_URL } from 'constants/environment'
import { CONFIRMATION_CODE_MINIMUM_LENGTH } from 'constants/settings'

// Styles
import './Form.scss'

// Section: Reset > Landing > Form
function Form () {
	// State
	const [formData, setFormData] = useState({ code: '', email: '', password: '', passwordConfirmation: '' })
	const [isCodeResent, setIsCodeResent] = useState(false)
	const [isInitialized, setIsInitialized] = useState(false)
	const [isPasswordVisible, setIsPasswordVisible] = useState(false)
	const [stage, setStage] = useState('information') // STAGES: 1. information, 2. code-verification, 3. password-reset

	// Data
	const { addNotice } = useNotices()
	const { translate } = useLanguage('pages.reset.views.landing.sections.form')
	const [searchParameters, setSearchParameters] = useSearchParams()
	const { isProcessing, reset } = usePasswordReset()
	const { isValid: isEmailValid, issues: emailIssues } = useFormControls(['isEmail'], formData.email)
	const { isValid: isPasswordValid, issues: passwordIssues } = useFormControls(
		['hasAtLeast8Characters', 'hasLowerCaseLetter', 'hasNumber', 'hasSpecialCharacter', 'hasUpperCaseLetter'],
		formData.password
	)
	const { isValid: isPasswordConfirmationValid, issues: passwordConfirmationIssues } = useFormControls(
		['hasMatchingValue'],
		formData.passwordConfirmation,
		formData.password
	)
	const isInformationReady = stage === 'information' && isEmailValid
	const isCodeVerificationReady = stage === 'code-verification' && formData.code.length >= CONFIRMATION_CODE_MINIMUM_LENGTH // TODO: Improve code and email check
	const isPasswordResetReady = (
		stage === 'password-reset' &&
		isPasswordValid &&
		isPasswordConfirmationValid
	) // TODO: Improve password (+confirmation) check

	// Functions
	function handleInput (type, value) {
		setFormData(formData => ({ ...formData, [type]: value }))
	}
	function handleSubmit () {
		if (isPasswordResetReady) reset(formData)
	}
	async function handleInformation () {
		await resendCode()
	}
	async function resendCode () {
		const isInitial = stage === 'information'
		const { email } = formData
		try {
			const response = await axios({ data: { email }, method: 'POST', url: PASSWORD_RESET_CODE_URL })
			if (isInitial) {
				if (response.status < 300) setStage('code-verification')
			} else setIsCodeResent(true)
		} catch (error) {
			let code = error?.response?.data?.message
				? `server.${error?.response?.data?.message}`
				: isInitial
					? 'client.passwordResetRequestError'
					: 'client.passwordResetCodeResendingError'
			addNotice({ code, log: error, type: 'error' })
		}
	}
	function togglePasswordVisibility () {
		setIsPasswordVisible(isPasswordVisible => !isPasswordVisible)
	}

	// Effects
	useEffect(() => {
		const email = searchParameters.get('email')
		if (!!email) {
			const code = searchParameters.get('code')
			setFormData(formData => ({ ...formData, code: code || '', email: email || '' }))
			setStage(code ? 'password-reset' : 'code-verification')
		}
		setSearchParameters({})
		setTimeout(() => setIsInitialized(true), 100)
	}, [searchParameters, setSearchParameters])
	useEffect(() => {
		let timer
		if (isCodeResent) timer = setTimeout(() => setIsCodeResent(false), 60 * 1000)
		return () => clearTimeout(timer)
	}, [isCodeResent])

	// Render
	return (
		<Page.Section
			willGrow
			willCenter
			name="form">
			<div className={
				'aix-authentication-form' +
				(isInitialized ? ' aix-initialized' : '')
			}>
				<Logo className="aix-logo" />
				<div className="aix-content">
					<form className="aix-form aix-paper-thick">
						<h2 className="aix-title">
							{stage === 'information' &&
								<span data-label={translate.global('generic.step', { number: 1 })}>
									{translate.local('form.title.information')}
								</span>
							}
							{stage === 'code-verification' &&
								<span data-label={translate.global('generic.step', { number: 2 })}>
									{translate.local('form.title.codeVerification')}
								</span>
							}
							{stage === 'password-reset' &&
								<span data-label={translate.global('generic.step', { number: 3 })}>
									{translate.local('form.title.passwordReset')}
								</span>
							}
						</h2>
						{stage === 'information' &&
							<>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={!!formData.email ? emailIssues.map(issue => translate.global(`notices.warning.form.${issue}Warning`)) : []}>
										<InputGroup
											disabled={isProcessing}
											name="email"
											type="email"
											value={formData.email}
											intent={Intent[(!formData.email || isEmailValid) ? 'NONE' : 'WARNING']}
											className="aix-input aix-email"
											placeholder={translate.local('form.fields.email.placeholder')}
											onChange={({ target }) => handleInput('email', target.value)} />
									</InputNotice>
								</FormGroup>
								<Button
									fill
									disabled={!isInformationReady || isProcessing}
									loading={isProcessing}
									className="aix-submit bp4-large"
									onClick={handleInformation}>
									{translate.local('form.actions.continue')}
								</Button>
							</>
						}
						{stage === 'code-verification' &&
							<>
								<FormGroup>
									<InputGroup
										disabled={isProcessing}
										name="code"
										type="text"
										value={formData.code}
										className="aix-input aix-code"
										intent={Intent[isEmailValid ? 'NONE' : 'WARNING']}
										placeholder={translate.local('form.fields.code.placeholder')}
										onChange={({ target }) => handleInput('code', target.value)} />
									<div className="aix-option">
										{isCodeResent
											? <>
												<Icon
													icon="tick"
													intent={Intent.SUCCESS}
													size={convertFontSizeToPixelSize('.5625rem')} />
												{translate.local('form.options.resendCode.label.success')}
											</>
											: <a onClick={resendCode}>{translate.local('form.options.resendCode.label.idle')}</a>
										}
									</div>
								</FormGroup>
								<Button
									fill
									disabled={!isCodeVerificationReady || isProcessing}
									loading={isProcessing}
									className="aix-submit bp4-large"
									onClick={() => setStage('password-reset')}>
									{translate.local('form.actions.continue')}
								</Button>
							</>
						}
						{stage === 'password-reset' &&
							<>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={!!formData.password ? passwordIssues.map(issue => translate.global(`notices.warning.form.${issue}Warning`)) : []}>
										<InputGroup
											disabled={isProcessing}
											name="password"
											type={isPasswordVisible ? 'text' : 'password'}
											value={formData.password}
											className="aix-input aix-password"
											intent={Intent[(!formData.password || isPasswordValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.password.placeholder')}
											onChange={({ target }) => handleInput('password', target.value)} />
									</InputNotice>
								</FormGroup>
								<FormGroup>
									<InputNotice
										intent={Intent.WARNING}
										list={!!formData.passwordConfirmation ? passwordConfirmationIssues.map(issue => translate.global(`notices.warning.form.${issue}Warning`)) : []}>
										<InputGroup
											disabled={isProcessing}
											name="passwordConfirmation"
											type={isPasswordVisible ? 'text' : 'password'}
											value={formData.passwordConfirmation}
											className="aix-input aix-password-confirmation"
											intent={Intent[(!formData.passwordConfirmation || isPasswordConfirmationValid) ? 'NONE' : 'WARNING']}
											placeholder={translate.local('form.fields.passwordConfirmation.placeholder')}
											onChange={({ target }) => handleInput('passwordConfirmation', target.value)} />
									</InputNotice>
									{/* TODO: Add password strength here */}
									<div className="aix-option">
										<Icon
											icon={isPasswordVisible ? 'eye-off' : 'eye-open'}
											size={convertFontSizeToPixelSize('.75rem')}
											intent={isPasswordVisible ? Intent.WARNING : Intent.DANGER}
											className="aix-option-icon" />
										<a
											className="aix-option-link"
											onClick={togglePasswordVisibility}>
											{isPasswordVisible
												? translate.local('form.fields.password.options.hide.label')
												: translate.local('form.fields.password.options.show.label')
											}
										</a>
									</div>
								</FormGroup>
								<Button
									fill
									disabled={!isPasswordResetReady || isProcessing}
									loading={isProcessing}
									className="aix-submit bp4-large"
									onClick={handleSubmit}>
									{translate.local('form.actions.changePassword')}
								</Button>
							</>
						}
					</form>
				</div>
			</div>
		</Page.Section>
	)
}

// Export
export default Form
